import React, { useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { usePreferences } from '../contexts/PreferencesContext';

type OutfeedSubTab = 'weighHopper' | 'pocketDelays';

interface OutfeedSettingsV2Props {
  outfeedData: {
    outfeedHopperCapacity: number | '';
    outfeedCycleWaitTime: number | '';
    outfeedWeighDelay: number | '';
    outfeedZeroDelay: number | '';
    outfeedZeroThresholdWeight: number | '';
    outfeedBreakoutAttempts: number | '';
    outfeedPocketBufferHopperCloseDelay: number | '';
    pocketCloseDelays: (number | '')[];
  };
  numberOfPockets: number;
  onChange: (field: string, value: any) => void;
  onPocketDelayChange: (index: number, value: any) => void;
  errors: Record<string, string>;
  isSystemDefaults?: boolean;
}

interface InputFieldProps {
  label: string;
  name: string;
  value: number | '';
  onChange: (value: any) => void;
  type?: string;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  tooltip?: string;
  unit?: string;
  error?: string;
}

const InputField: React.FC<InputFieldProps> = ({
  label,
  name,
  value,
  onChange,
  type = 'number',
  min,
  max,
  step,
  placeholder,
  tooltip,
  unit,
  error
}) => {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label htmlFor={name} className="text-sm font-medium text-slate-700">
          {label}
        </label>
        {tooltip && (
          <div className="group relative">
            <InformationCircleIcon className="h-4 w-4 text-slate-400 hover:text-slate-600 cursor-help" />
            <div className="absolute z-10 invisible group-hover:visible bg-slate-800 text-white text-xs rounded-md py-2 px-3 bottom-6 right-0 w-64 shadow-lg">
              {tooltip}
            </div>
          </div>
        )}
      </div>
      <div className="relative">
        <input
          type={type}
          id={name}
          name={name}
          value={value}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
            error ? 'border-red-300' : 'border-slate-300'
          } ${unit ? 'pr-12' : ''}`}
        />
        {unit && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">
            {unit}
          </span>
        )}
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

const OutfeedSettingsV2: React.FC<OutfeedSettingsV2Props> = ({
  outfeedData,
  numberOfPockets,
  onChange,
  onPocketDelayChange,
  errors,
  isSystemDefaults = false
}) => {
  const [activeSubTab, setActiveSubTab] = useState<OutfeedSubTab>('weighHopper');
  const { weightUnit } = usePreferences();

  return (
    <div className="space-y-6">
      {/* Sub-tabs Navigation */}
      <div className="border-b border-slate-200">
        <nav className="-mb-px flex space-x-6">
          <button
            type="button"
            onClick={() => setActiveSubTab('weighHopper')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeSubTab === 'weighHopper' 
                ? 'border-indigo-500 text-indigo-600' 
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            Station Weigh Hopper
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('pocketDelays')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeSubTab === 'pocketDelays' 
                ? 'border-indigo-500 text-indigo-600' 
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            Pocket Delays
          </button>
        </nav>
      </div>

      {/* Sub-tab Content */}
      {activeSubTab === 'weighHopper' ? (
        <div className="pt-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">
            Station Weigh Hopper Settings
          </h3>
          <p className="text-sm text-slate-600 mb-6">
            Configure the default timing and weight settings for the outfeed weigh hopper operations.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Hopper Capacity"
              name="outfeedHopperCapacity"
              value={outfeedData.outfeedHopperCapacity}
              onChange={(value) => onChange('outfeedHopperCapacity', value)}
              min={0}
              step={0.01}
              placeholder="100"
              unit={weightUnit === 'KGS' ? 'kg' : 'lbs'}
              tooltip="Maximum weight capacity of the outfeed hopper"
              error={errors.outfeedHopperCapacity}
            />
            
            <InputField
              label="Cycle Wait Time"
              name="outfeedCycleWaitTime"
              value={outfeedData.outfeedCycleWaitTime}
              onChange={(value) => onChange('outfeedCycleWaitTime', value)}
              min={0}
              placeholder="1000"
              unit="ms"
              tooltip="Time to wait between outfeed cycles"
              error={errors.outfeedCycleWaitTime}
            />
            
            <InputField
              label="Weigh Delay"
              name="outfeedWeighDelay"
              value={outfeedData.outfeedWeighDelay}
              onChange={(value) => onChange('outfeedWeighDelay', value)}
              min={0}
              placeholder="500"
              unit="ms"
              tooltip="Delay before taking weight measurement"
              error={errors.outfeedWeighDelay}
            />
            
            <InputField
              label="Zero Delay"
              name="outfeedZeroDelay"
              value={outfeedData.outfeedZeroDelay}
              onChange={(value) => onChange('outfeedZeroDelay', value)}
              min={0}
              placeholder="500"
              unit="ms"
              tooltip="Delay for zeroing the scale"
              error={errors.outfeedZeroDelay}
            />
            
            <InputField
              label="Zero Threshold Weight"
              name="outfeedZeroThresholdWeight"
              value={outfeedData.outfeedZeroThresholdWeight}
              onChange={(value) => onChange('outfeedZeroThresholdWeight', value)}
              min={0}
              step={0.001}
              placeholder="0.05"
              unit={weightUnit === 'KGS' ? 'kg' : 'lbs'}
              tooltip="Weight threshold for automatic zeroing"
              error={errors.outfeedZeroThresholdWeight}
            />
            
            <InputField
              label="Breakout Attempts"
              name="outfeedBreakoutAttempts"
              value={outfeedData.outfeedBreakoutAttempts}
              onChange={(value) => onChange('outfeedBreakoutAttempts', value)}
              min={0}
              placeholder="3"
              tooltip="Number of attempts to break out stuck product"
              error={errors.outfeedBreakoutAttempts}
            />
            
            <InputField
              label="Pocket Buffer Hopper Close Delay"
              name="outfeedPocketBufferHopperCloseDelay"
              value={outfeedData.outfeedPocketBufferHopperCloseDelay}
              onChange={(value) => onChange('outfeedPocketBufferHopperCloseDelay', value)}
              min={0}
              placeholder="500"
              unit="ms"
              tooltip="General delay for closing pocket buffer hoppers"
              error={errors.outfeedPocketBufferHopperCloseDelay}
            />
          </div>
        </div>
      ) : (
        <div className="pt-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">
            Pocket Close Delays
          </h3>
          <p className="text-sm text-slate-600 mb-6">
            Configure individual close delay timings for each pocket. These delays determine how long to wait before closing each pocket's gate after product discharge.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: numberOfPockets }, (_, index) => (
              <InputField
                key={index}
                label={`Pocket ${index + 1} Close Delay`}
                name={`pocketCloseDelay${index}`}
                value={outfeedData.pocketCloseDelays[index] || ''}
                onChange={(value) => onPocketDelayChange(index, value)}
                min={0}
                placeholder="500"
                unit="ms"
                tooltip={`Delay before closing pocket ${index + 1} gate`}
                error={errors[`pocketCloseDelay${index}`]}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OutfeedSettingsV2;